import React, { useContext, useEffect, useReducer } from "react"
import dynamic from "next/dynamic"
import { GetServerSideProps } from "next"
import { Container, Box, Grid } from "@mui/material"
import { makeStyles } from "makeStyles"
import Rating from "@mui/material/Rating"
import { useSnackbar } from "notistack"

import NotFoundPage from "pages/404"
import SEO from "@secureo/common/components/SEO"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"
import ImageSlider from "containers/ProductDetailPage/ImageSlider"
import ShortDescription from "containers/ProductDetailPage/ShortDescription"
import PriceAndDelivery from "containers/ProductDetailPage/PriceAndDelivery"

const StickyPricingTablet = dynamic(
	() => import("containers/ProductDetailPage/PriceComponents/StickyPricingTablet"),
	{
		ssr: false,
	},
)
import Accordion from "containers/ProductDetailPage/Accordion/Accordion"
import CylinderConfigurations from "containers/ProductDetailPage/Configurations/Cylinder/CylinderConfigurations"
import SimpleConfiguration from "containers/ProductDetailPage/Configurations/SimpleConfiguration"
import IntermediateCart from "containers/ProductDetailPage/IntermediateCart"
import ProductStructuredData from "@secureo/common/components/StructuredData/ProductStructuredData"
import ConfigurationGroupsWithFloatingPrice from "containers/ProductDetailPage/Configurations/ConfigurationGroups/ConfigurationGroupsWithFloatingPrice"
import Link from "components/i18n/Link"
const CrossSellProducts = dynamic(
	() => import("../../../containers/ProductDetailPage/CrossSellProducts"),
	{
		ssr: false,
	},
)

import useProductReviews from "@secureo/common/hooks/data/useProductReviews"
import useWindowSize from "@secureo/common/hooks/useWindowSize"

import AppContext from "@secureo/common/context/AppContext/AppContext"
import reducer, {
	setQuantity,
	addProductAndConfigurationsToCart,
	resetReducerState,
	DispatchProductDetailPageReducer,
	ProductDetailPageReducerState,
	addProductAndConfigurationsToShoppingList,
} from "@secureo/common/reducers/productDetailPageReducer"
import getInitialReducerState from "@secureo/common/reducers/productDetailPageReducer/utils/getInitialReducerState"

import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchProduct from "@secureo/common/utils/commerceTools/graphql/queries/fetchProduct"
import {
	trackProductDetailImpression,
	trackGA4ViewItem,
} from "@secureo/common/utils/tracking/productDetail"
import { isGoogleBotUserAgent } from "@secureo/common/utils/detection/userAgent"
import fetchProductReviewsByProductId from "@secureo/common/utils/commerceTools/graphql/queries/fetchProductReviewsByProductId"
import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import serializeJSON from "@secureo/common/utils/serializeJSON"

import { CustomAppProps } from "pages/_app"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import { Product, Review } from "@secureo/common/typings/Product"
import isBrowser from "@secureo/common/utils/isBrowser"

const useStyles = makeStyles()({
	titlePositionMobile: {
		display: "none",
		"@media only screen and (max-width: 960px)": {
			display: "block",
			lineHeight: 1.05,
			fontSize: "90%",
		},
	},
	titlePositionDesktop: {
		display: "block",
		"@media only screen and (max-width: 960px)": {
			display: "none",
		},
	},
	desktopConfiguration: {
		display: "none",

		"@media only screen and (min-width: 960px)": {
			display: "block",
			padding: 24,
		},
	},
	mobileConfiguration: {
		display: "block",
		padding: 20,
		"@media only screen and (min-width: 960px)": {
			display: "none",
		},
	},
})

const Title = ({ name, reviewRatingStatistics }: TitleProps) => {
	const { averageRating } = reviewRatingStatistics
	return (
		<>
			<h1 style={{ margin: "0px 0px 10px 0px", padding: 0, fontSize: 18, lineHeight: 1.3 }}>
				<strong>{name}</strong>
			</h1>
			<div>
				<a href="#ratings">
					<Rating name="read-only" precision={0.5} value={averageRating} readOnly />
				</a>
			</div>
		</>
	)
}

interface TitleProps {
	name: string
	reviewRatingStatistics: any
}

const ProductPage = ({
	product,
	reviews: ssrReviews,
	slug,
	language,
	countryCode,
	locale,
	isDebug,
	initialVariantSku: initialSSRVariantSku,
}: Props & CustomAppProps) => {
	const { classes } = useStyles()
	const { enqueueSnackbar } = useSnackbar()
	const appContextState = useContext(AppContext)

	const { innerWidth } = useWindowSize()
	const isMobile = innerWidth < 600

	const {
		metaTitle,
		metaDescription,
		ancestorBreadCrumbs = [],
		reviewRatingStatistics,
		lockingSystemCategorySlug,
	} = product ?? {}
	const mostSpecificBreadCrumbCategoryName = [...ancestorBreadCrumbs].reverse()?.[0]?.label ?? ""

	const initialVariantSku = isBrowser()
		? new URLSearchParams(window.location.search).get("variant")
		: initialSSRVariantSku

	const [reducerState, dispatch] = useReducer(
		reducer,
		getInitialReducerState(product, countryCode, locale, "DZN", isDebug, initialVariantSku),
	) as [ProductDetailPageReducerState, DispatchProductDetailPageReducer]

	const {
		isLoading,
		showIntermediateShoppingCart,
		variantsByVariantId = {},
		name,
		shortDescription,
		images,
		attributes = {},

		// Price
		quantity,
		taxRate,
		singleGrossPriceCents,

		// Msrp price
		singleGrossMsrpPriceCents,

		// Cylinder
		cylinderLockingMechanismProducts,
		cylinderConfigurationType,
	} = reducerState

	const configurationGroups = attributes?.configurationGroups ?? []

	const accessToken = appContextState?.accessToken ?? null
	const masterVariantSku = variantsByVariantId?.[1]?.sku
	const numberOfVariants = Object.keys(variantsByVariantId).length
	const crossSellProductSkus = attributes?.crossSellProductSkusByCountryCode?.[countryCode] ?? []
	const upSellProductSkus = attributes?.upSellProductSkusByCountryCode?.[countryCode] ?? []
	const hasMainConfiguration =
		!!cylinderConfigurationType || !!cylinderLockingMechanismProducts || numberOfVariants > 1

	useEffect(() => {
		// Prevent 404 tracking
		if (product?.id) {
			resetReducerState(
				dispatch,
				product,
				countryCode,
				"DZN",
				locale,
				isDebug,
				initialVariantSku,
			)

			trackProductDetailImpression(
				name,
				masterVariantSku,
				singleGrossPriceCents,
				attributes.manufacturer,
				mostSpecificBreadCrumbCategoryName,
				mostSpecificBreadCrumbCategoryName,
			)

			trackGA4ViewItem(
				name,
				masterVariantSku,
				singleGrossPriceCents,
				attributes.manufacturer,
				mostSpecificBreadCrumbCategoryName,
			)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product?.id, countryCode])

	const { reviews } = useProductReviews(accessToken, product?.id, ssrReviews)

	if (!product) return <NotFoundPage />

	const addToCart = async () => {
		await addProductAndConfigurationsToCart(
			dispatch,
			appContextState,
			reducerState,
			language,
			countryCode,
			mostSpecificBreadCrumbCategoryName,
			"EUR",
		)
		enqueueSnackbar("Zum Warenkorb hinzugefügt", {
			variant: "success",
		})
	}

	const addToShoppingList = async () => {
		await addProductAndConfigurationsToShoppingList(dispatch, appContextState, reducerState)
		enqueueSnackbar("Zur Wunschliste hinzugefügt", {
			variant: "success",
		})
	}

	const updateQuantity = (quantity: number) => {
		setQuantity(dispatch, reducerState, quantity)
	}

	const asPath = `/${locale}/p/${slug}`
	const breadcrumb: BreadCrumb = {
		label: name,
		href: asPath,
	}

	return (
		<>
			<SEO
				title={metaTitle}
				description={metaDescription}
				pageType="product"
				productName={name}
				asPath={asPath}
			/>
			<BreadCrumbs currentPath={breadcrumb} locale={locale} ancestors={ancestorBreadCrumbs} />
			<Container style={{ marginTop: 15 }}>
				<Grid container spacing={3}>
					<Grid item xs={12} className={classes.titlePositionMobile}>
						<Title name={name} reviewRatingStatistics={reviewRatingStatistics} />
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<div style={{ position: "relative" }}>
							<ImageSlider images={images} />
						</div>
					</Grid>
					<Grid item xs={12} sm={6} md={8}>
						<Grid container spacing={3}>
							<Grid item xs={12} lg={hasMainConfiguration ? 8 : 12}>
								<Box mt={2} className={classes.titlePositionDesktop}>
									<Title
										name={name}
										reviewRatingStatistics={reviewRatingStatistics}
									/>
								</Box>
								<Box mt={2}>
									<ShortDescription shortDescription={shortDescription} />
								</Box>
								<CylinderConfigurations
									dispatch={dispatch}
									reducerState={reducerState}
									accessToken={accessToken}
								/>
								{numberOfVariants > 1 && (
									<SimpleConfiguration
										isLoading={isLoading}
										dispatch={dispatch}
										reducerState={reducerState}
									/>
								)}
								{lockingSystemCategorySlug && (
									<Link
										href={`/locking-system/${lockingSystemCategorySlug}`}
										style={{
											fontSize: "0.85rem",
											textDecoration: "underline",
											color: "inherit",
										}}
									>
										Schließanlage erstellen
									</Link>
								)}
							</Grid>
							<Grid item xs={12} lg={hasMainConfiguration ? 4 : 12}>
								<PriceAndDelivery
									accessToken={accessToken}
									reducerState={reducerState}
									setQuantity={updateQuantity}
									addToCart={addToCart}
									addToShoppingList={addToShoppingList}
									hasMainConfiguration={hasMainConfiguration}
								/>
							</Grid>
							<div className={classes.desktopConfiguration}>
								{accessToken && configurationGroups.length > 0 && (
									<Grid item xs={12}>
										<h2 style={{ fontSize: 20 }}>Konfigurationen</h2>
										<ConfigurationGroupsWithFloatingPrice
											accessToken={accessToken}
											dispatch={dispatch}
											reducerState={reducerState}
											addToCart={addToCart}
											setQuantity={updateQuantity}
											addToShoppingList={addToShoppingList}
										/>
									</Grid>
								)}
							</div>
						</Grid>
					</Grid>

					<div className={classes.mobileConfiguration}>
						{accessToken && configurationGroups.length > 0 && (
							<Grid item xs={12}>
								<h2 style={{ fontSize: 20 }}>Konfigurationen</h2>
								<ConfigurationGroupsWithFloatingPrice
									accessToken={accessToken}
									dispatch={dispatch}
									reducerState={reducerState}
									addToCart={addToCart}
									setQuantity={updateQuantity}
									addToShoppingList={addToShoppingList}
								/>
							</Grid>
						)}
					</div>

					<Grid item xs={12}>
						{accessToken && crossSellProductSkus.length > 0 && (
							<CrossSellProducts
								accessToken={accessToken}
								crossSellProductSkus={crossSellProductSkus}
								countryCode={countryCode}
								taxRate={taxRate}
								masterVariantSku={masterVariantSku}
							/>
						)}
					</Grid>
				</Grid>
			</Container>
			<Accordion
				accessToken={accessToken}
				productId={product?.id ?? null}
				masterVariantSku={masterVariantSku}
				reducerState={reducerState}
				reviewRatingStatistics={reviewRatingStatistics}
				reviews={reviews}
				setQuantity={updateQuantity}
				addToCart={addToCart}
				addToShoppingList={addToShoppingList}
			/>
			<ProductStructuredData
				product={product}
				reviews={reviews}
				locale={locale}
				singleGrossPriceCents={singleGrossPriceCents}
			/>
			<StickyPricingTablet
				singleGrossPriceCents={singleGrossPriceCents}
				singleGrossMsrpPriceCents={singleGrossMsrpPriceCents}
				setQuantity={updateQuantity}
				addToCart={addToCart}
				isLoading={isLoading}
				quantity={quantity}
			/>
			{showIntermediateShoppingCart && !isMobile && (
				<IntermediateCart
					dispatch={dispatch}
					cart={appContextState.cart}
					upSellProductSkus={upSellProductSkus}
					masterVariantSku={masterVariantSku}
					countryCode={countryCode}
				/>
			)}
		</>
	)
}

interface Props {
	product: Product
	reviews: Review[]
	slug: string
	locale: string
	language: string
	countryCode: CountryCode
	isDebug: boolean
	initialVariantSku: string
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { req, query, res } = context

	const accessToken = await anonymousAccessTokenCache()
	const { slug, locale: rawLocale } = context.params as {
		locale: string
		slug: string
	}

	const isDebug = query.debug !== undefined
	const initialVariantSku = query.variant as string

	const { locale, language, countryCode } = normalizeLocale(rawLocale)
	const product = await fetchProduct(accessToken, slug, locale, "DZN")

	if (!product) {
		res.statusCode = 404
		return {
			props: {
				error: "Product not found",
			},
		}
	}

	let reviews: Review[] = []

	// Reviews are only fetched on SSR, if request user agent matches Google Bot (So it can be included in structured data)
	if (product && isGoogleBotUserAgent(req)) {
		reviews = await fetchProductReviewsByProductId(accessToken, product?.id)
	}

	const props: Props = {
		product,
		reviews,
		slug,
		locale,
		language,
		countryCode,
		isDebug,
		initialVariantSku,
	}

	res.setHeader("Cache-Control", "s-maxage=5, stale-while-revalidate=5")

	return {
		props: serializeJSON(props),
	}
}

export default ProductPage
